import { supabase } from "../../../lib/supabase";
import { formatBookingEffLabel } from "./botAdminShared";
import type { BookingInsight } from "./botAdminShared";

export type BotMasterContainerPayload = {
  master: {
    id: number;
    reference: string | null;
    status: string | null;
    etd: string | null;
    delayed_etd: string | null;
    effective_etd: string | null;
    eta: string | null;
    eta_customer: string | null;
    track_and_trace: string | null;
    origin_port: { name: string | null; country: string | null } | null;
    destination_port: { name: string | null; country: string | null } | null;
    transport_type: {
      name: string | null;
      abbreviation: string | null;
    } | null;
  };
  /**
   * Other bookings on this master B/L — STRICTLY same company_id only.
   * Never includes bookings from other companies.
   */
  same_company_sibling_bookings: Array<{
    id: number;
    eff_reference: string;
    reference: string | null;
    status: string | null;
    container_id: number | null;
    container_reference: string | null;
  }>;
  /** How many other containers sit on this master (no booking PII). */
  linked_container_count: number;
  privacy_note: string;
};

function unwrapOne<T>(value: T | T[] | null | undefined): T | null {
  if (value == null) return null;
  return Array.isArray(value) ? (value[0] ?? null) : value;
}

/**
 * Load master B/L data for the booking's container.
 * Sibling bookings are included ONLY when company_id matches — never other tenants.
 */
export async function fetchBotMasterContainer(
  booking: BookingInsight,
): Promise<BotMasterContainerPayload | null> {
  const companyId =
    typeof booking.company_id === "number"
      ? booking.company_id
      : Number(booking.company_id);
  if (!Number.isFinite(companyId)) return null;

  const containerRaw = booking.container as
    | { id?: number }
    | { id?: number }[]
    | null
    | undefined;
  const container = unwrapOne(containerRaw);
  const containerId =
    typeof container?.id === "number"
      ? container.id
      : typeof booking.container_id === "number"
        ? booking.container_id
        : Number(booking.container_id);

  if (!Number.isFinite(containerId)) return null;

  const { data: link, error: linkError } = await supabase
    .from("master_containers_containers")
    .select("master_container_id")
    .eq("container_id", containerId)
    .maybeSingle();

  if (linkError) {
    console.warn("Master container link lookup:", linkError);
    return null;
  }

  const masterId = link?.master_container_id;
  if (typeof masterId !== "number") return null;

  const { data: masterRow, error: masterError } = await supabase
    .from("master_containers")
    .select(
      `
      id, reference, status, etd, delayed_etd, eta, eta_customer, track_and_trace,
      origin_port_info:booking_origin_port(name, country),
      destination_port_info:booking_destination_port(name, country),
      transport_type_info:booking_transport_types(name, abbreviation)
    `,
    )
    .eq("id", masterId)
    .maybeSingle();

  if (masterError) {
    console.warn("Master container fetch:", masterError);
    return null;
  }
  if (!masterRow) return null;

  const { data: links, error: linksError } = await supabase
    .from("master_containers_containers")
    .select("container_id")
    .eq("master_container_id", masterId);

  if (linksError) {
    console.warn("Master container containers:", linksError);
  }

  const containerIds = (links ?? [])
    .map((row) => row.container_id)
    .filter((id): id is number => typeof id === "number");

  // CRITICAL: same company only — never return other companies' bookings.
  let siblings: BotMasterContainerPayload["same_company_sibling_bookings"] = [];

  if (containerIds.length > 0) {
    const { data: siblingRows, error: siblingError } = await supabase
      .from("bookings")
      .select(
        `
        id, reference, status, company_id, container_id,
        transport_type:booking_transport_types(abbreviation),
        container:containers(reference)
      `,
      )
      .in("container_id", containerIds)
      .eq("company_id", companyId)
      .neq("id", booking.id)
      .order("id", { ascending: true });

    if (siblingError) {
      console.warn("Master sibling bookings:", siblingError);
    } else {
      siblings = (siblingRows ?? [])
        // Defense in depth: drop anything that somehow isn't this company
        .filter((row) => row.company_id === companyId)
        .map((row) => {
          const transport = unwrapOne(
            row.transport_type as
              | { abbreviation?: string | null }
              | { abbreviation?: string | null }[]
              | null,
          );
          const cont = unwrapOne(
            row.container as
              | { reference?: string | null }
              | { reference?: string | null }[]
              | null,
          );
          return {
            id: row.id as number,
            eff_reference: formatBookingEffLabel({
              id: row.id as number,
              reference: (row.reference as string | null) ?? null,
              transport_type: transport,
            }),
            reference: (row.reference as string | null) ?? null,
            status: (row.status as string | null) ?? null,
            container_id:
              typeof row.container_id === "number" ? row.container_id : null,
            container_reference: cont?.reference ?? null,
          };
        });
    }
  }

  const origin = unwrapOne(
    masterRow.origin_port_info as
      | { name?: string | null; country?: string | null }
      | { name?: string | null; country?: string | null }[]
      | null,
  );
  const destination = unwrapOne(
    masterRow.destination_port_info as
      | { name?: string | null; country?: string | null }
      | { name?: string | null; country?: string | null }[]
      | null,
  );
  const transport = unwrapOne(
    masterRow.transport_type_info as
      | { name?: string | null; abbreviation?: string | null }
      | { name?: string | null; abbreviation?: string | null }[]
      | null,
  );

  return {
    master: {
      id: masterRow.id as number,
      reference: (masterRow.reference as string | null) ?? null,
      status: (masterRow.status as string | null) ?? null,
      etd: (masterRow.etd as string | null) ?? null,
      delayed_etd: (masterRow.delayed_etd as string | null) ?? null,
      effective_etd:
        ((masterRow.delayed_etd as string | null) ||
          (masterRow.etd as string | null)) ??
        null,
      eta: (masterRow.eta as string | null) ?? null,
      eta_customer: (masterRow.eta_customer as string | null) ?? null,
      track_and_trace: (masterRow.track_and_trace as string | null) ?? null,
      origin_port: origin
        ? { name: origin.name ?? null, country: origin.country ?? null }
        : null,
      destination_port: destination
        ? {
            name: destination.name ?? null,
            country: destination.country ?? null,
          }
        : null,
      transport_type: transport
        ? {
            name: transport.name ?? null,
            abbreviation: transport.abbreviation ?? null,
          }
        : null,
    },
    same_company_sibling_bookings: siblings,
    linked_container_count: containerIds.length,
    privacy_note:
      "same_company_sibling_bookings only includes bookings with the same company_id. Other companies on this master B/L are never exposed.",
  };
}
